import React, { useState } from "react";
import styled from "styled-components";
import { Card, Typography, Button, message } from "antd";
import { CrownOutlined } from "@ant-design/icons";
import { getApiUrl } from "../../lib/api/config";
import { isLaunchDemoMode } from "../../helpers/constants";

const { Paragraph, Text } = Typography;

/**
 * Leo-Verified ($49/mo) upsell on public vendor bid / negotiate flows.
 */
export default function PreferredEdgePromo({
  bidToken,
  returnPage = "bid",
  isPreferred,
  onPreferredChange,
}) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const demo = isLaunchDemoMode();

  const startCheckout = async () => {
    if (!bidToken) return;
    setCheckoutLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/vendor-bids/leo-verified/checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bidToken, returnPage }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Could not start checkout");
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      message.error(e.message || "Checkout failed");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const simulateUpgrade = async () => {
    if (!bidToken) return;
    setSimLoading(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/vendor-bids/bid/${encodeURIComponent(
          bidToken
        )}/demo-preferred-toggle`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Simulate failed");
      }
      message.success(
        data.isPreferred
          ? "Preferred status ON (demo)"
          : "Preferred status OFF (demo)"
      );
      onPreferredChange?.(Boolean(data.isPreferred));
    } catch (e) {
      message.error(e.message || "Simulate failed");
    } finally {
      setSimLoading(false);
    }
  };

  if (isPreferred) {
    return (
      <PromoCard size="small" bordered={false}>
        <CrownOutlined style={{ color: "#ca8a04", fontSize: 22, marginBottom: 8 }} />
        <Text strong style={{ display: "block", marginBottom: 4 }}>
          You have the Preferred edge
        </Text>
        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
          Clumse AI Verified is active. You will show the verified badge and preferred
          bid visibility benefits on this account.
        </Paragraph>
      </PromoCard>
    );
  }

  return (
    <PromoCard
      title={
        <span>
          <CrownOutlined style={{ marginRight: 8, color: "#ca8a04" }} />
          Get the Preferred Edge
        </span>
      }
      size="small"
    >
      <Paragraph style={{ fontSize: 14, marginBottom: 12 }}>
        Subscribe to <strong>Clumse AI Verified</strong> for <strong>$49/month</strong>
        : stand out with a verified badge, preferred visibility, and
        bid tools designed for vendors who win more work on Clumse.
      </Paragraph>
      <Button
        type="primary"
        block
        size="large"
        loading={checkoutLoading}
        onClick={startCheckout}
      >
        Subscribe with Stripe ($49/mo)
      </Button>
      {demo ? (
        <DemoButton
          block
          style={{ marginTop: 10 }}
          loading={simLoading}
          onClick={simulateUpgrade}
        >
          Simulate Upgrade (demo)
        </DemoButton>
      ) : null}
    </PromoCard>
  );
}

const PromoCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
`;

const DemoButton = styled(Button)`
  border-style: dashed;
`;
